import React, { useEffect, useMemo, useRef } from 'react';

/**
 * RadioGroup Component
 * --------------------
 * A styled radio button group that supports:
 * - Standard single selection
 * - Disabled options with "coming soon" styling (greyed out, non-selectable)
 * - Free text input for options that allow additional details
 * - Help text displayed as small italicized text under the label
 * 
 * Option Properties:
 *   - optionId (string): Unique identifier for the option
 *   - label (string): Display text for the option
 *   - helpText (string): Optional supplementary text shown below the label in italics
 *   - disabled (boolean): If true, shows "Coming soon" badge and prevents selection
 *   - disabledReason (string): Optional reason shown when disabled
 *   - allowsFreeText (boolean): If true, shows a textarea for additional details
 */
export default function RadioGroup({ name, label, options = [], value, onChange, required, afterLabel = null, freeTextError = null }) {
  const selectedId = useMemo(() => (value?.optionId ?? value) || '', [value]);
  const otherText = useMemo(() => (value && value.otherText) || '', [value]);
  // Map of optionId -> textarea element refs for options that allow free text
  const freeTextRefs = useRef({});
  // Track whether we just toggled into a free‑text option so we can place the caret
  // without selecting existing text. Avoid auto‑selecting on initial mount.
  const shouldFocusFreeTextRef = useRef(false);
  useEffect(() => {
    if (!selectedId) return;
    const selected = (options || []).find(o => o.optionId === selectedId);
    if (!selected || !selected.allowsFreeText) return;
    const el = freeTextRefs.current[selectedId];
    if (!el) return;
    if (shouldFocusFreeTextRef.current) {
      try {
        el.focus({ preventScroll: true });
        const len = (el.value || '').length;
        if (typeof el.setSelectionRange === 'function') {
          el.setSelectionRange(len, len);
        }
      } catch (_) {}
      shouldFocusFreeTextRef.current = false;
    }
  }, [selectedId, options]);

  // Refs to each option tile so arrow-key navigation can move focus (TD-17).
  const tileRefs = useRef({});
  const enabledOptions = (options || []).filter(o => o.disabled !== true);
  const selectedExists = enabledOptions.some(o => o.optionId === selectedId);

  return (
    <div className="mb-4">
      <div id={`${name}-label`} className="text-sm font-medium text-gray-700 mb-2">
        {label}{required ? <span className="text-red-600"> *</span> : null}
      </div>
      {afterLabel}
      <div className="space-y-2" role="radiogroup" aria-labelledby={`${name}-label`} aria-required={required || undefined}>
        {options.map(opt => {
          const isDisabled = opt.disabled === true;
          const selected = selectedId === opt.optionId;
          const showOther = selected && opt.allowsFreeText === true;
          // Roving tabindex: only the selected option (or the first enabled
          // option when none is selected) is in the tab order.
          const isFirstEnabled = !isDisabled && enabledOptions[0]?.optionId === opt.optionId;
          const tabbable = !isDisabled && (selected || (!selectedExists && isFirstEnabled));
          
          // Handle click - disabled options should not be selectable
          const handleSelect = () => {
            if (isDisabled) return; // Prevent selection of disabled options
            
            if (opt.allowsFreeText) {
              // Mark to focus the textarea after it appears; preserve existing text if present.
              shouldFocusFreeTextRef.current = true;
              const preserved = (value && value?.optionId === opt.optionId) ? (value.otherText || '') : '';
              onChange({ optionId: opt.optionId, otherText: preserved });
            } else {
              onChange({ optionId: opt.optionId });
            }
          };

          // Keyboard support (TD-17): Space/Enter selects; arrow keys move to and
          // select the previous/next enabled option, matching native radio groups.
          const onKeyDown = (e) => {
            if (isDisabled) return;
            // Ignore keys bubbling up from the nested free-text field, otherwise
            // Space/Enter would be swallowed (preventDefault) before reaching the
            // textarea and words would run together (TD-128).
            if (e.target !== e.currentTarget) return;
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              handleSelect();
              return;
            }
            if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) {
              if (enabledOptions.length === 0) return;
              e.preventDefault();
              const here = enabledOptions.findIndex(o => o.optionId === opt.optionId);
              const dir = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1 : -1;
              const next = enabledOptions[(here + dir + enabledOptions.length) % enabledOptions.length];
              if (!next) return;
              onChange(next.allowsFreeText ? { optionId: next.optionId, otherText: '' } : { optionId: next.optionId });
              const el = tileRefs.current[next.optionId];
              if (el && typeof el.focus === 'function') { try { el.focus({ preventScroll: true }); } catch (_) {} }
            }
          };

          return (
            <div
              key={opt.optionId}
              ref={(el) => { tileRefs.current[opt.optionId] = el; }}
              className={`group p-3 rounded-xl border-2 transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-primary ${
                isDisabled
                  ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50'
                  : selected
                    ? 'cursor-pointer border-primary bg-primary/5 shadow-sm'
                    : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300'
              }`}
              onClick={handleSelect}
              onKeyDown={onKeyDown}
              tabIndex={isDisabled ? -1 : (tabbable ? 0 : -1)}
              role="radio"
              aria-checked={selected}
              aria-disabled={isDisabled}
              // Named by its own label, not computed from everything inside
              // the tile (help text, a free-text box), which some screen
              // readers announce as "no label".
              aria-labelledby={`${name}-${opt.optionId}-label`}
              aria-describedby={opt.helpText ? `${name}-${opt.optionId}-help` : undefined}
            >
              {/* Presentational content; the option tile above is the radio
                  control (role="radio"). No native <input> — the component is
                  controlled and the div handles selection + keyboard (TD-17). */}
              <div className={`flex items-start gap-3 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <span
                  className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-full border-2 transition-all duration-200 ease-out relative ${
                    isDisabled 
                      ? 'border-gray-300 bg-gray-100' 
                      : selected 
                        ? 'bg-yellow-400 border-yellow-400' 
                        : 'border-gray-300 bg-white group-hover:border-gray-400'
                  }`}
                  aria-hidden="true"
                >
                  {selected && !isDisabled && (
                    <svg viewBox="0 0 16 16" className="absolute inset-0 w-3.5 h-3.5 m-auto fill-white">
                      <path d="M6.173 12.414a1 1 0 0 1-1.414 0L1.88 9.535a1 1 0 1 1 1.415-1.415l2.18 2.18 7.23-7.23a1 1 0 1 1 1.415 1.414l-8.947 8.93z"/>
                    </svg>
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <span id={`${name}-${opt.optionId}-label`} className={`text-sm font-medium transition-colors duration-200 ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                    {opt.label}
                  </span>
                  {/* Help text shown as small italicized text below the label */}
                  {opt.helpText && (
                    <p id={`${name}-${opt.optionId}-help`} className={`text-xs italic mt-0.5 ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                      {opt.helpText}
                    </p>
                  )}
                </div>
                {/* "Coming soon" badge for disabled options */}
                {isDisabled && (
                  <span className="flex-shrink-0 text-xs font-medium text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                    Coming soon
                  </span>
                )}
              </div>
              <div
                className={`transition-all duration-300 overflow-hidden ${showOther ? 'max-h-36 mt-2' : 'max-h-0 mt-0'}`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {opt.allowsFreeText && (
                  <>
                    <textarea
                      ref={(el) => { freeTextRefs.current[opt.optionId] = el; }}
                      className="resize-none w-[calc(100%-theme(spacing.1)*2)] mx-1 mt-1 p-2 border rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary border-gray-200 box-border"
                      placeholder={opt.freeTextPlaceholder || opt.placeholder || 'Please provide details'}
                      value={showOther ? otherText : ''}
                      onChange={(e) => onChange({ optionId: opt.optionId, otherText: e.target.value })}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      rows={3}
                      aria-invalid={showOther && freeTextError && String(otherText || '').trim() !== '' ? true : undefined}
                      aria-describedby={showOther && freeTextError && String(otherText || '').trim() !== ''
                        ? `${name}-${opt.optionId}-error` : undefined}
                    />
                    {/* A required follow-up quietly disables the next button;
                        say why, right where the answer goes, until it has one
                        (staging E2E feedback, 26 Oct). */}
                    {showOther && opt.freeTextOptional !== true
                      && String(otherText || '').trim() === '' && (
                      <p className="mx-1 mt-1 text-sm text-gray-600 text-left">
                        Add an answer here to continue to the next question.
                      </p>
                    )}
                    {/* What was typed fails the option's validator (the NHS
                        number, TDI-510): say what a right answer looks like,
                        in the same place, with the icon and the colour. */}
                    {showOther && freeTextError && String(otherText || '').trim() !== '' && (
                      <p id={`${name}-${opt.optionId}-error`} role="alert"
                         className="mx-1 mt-1 flex items-start gap-1 text-sm text-red-700 text-left">
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5 flex-shrink-0">
                          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M8 4.5v4M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        <span>{freeTextError}</span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


